import type { i18n as I18n } from 'i18next';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { I18nextProvider, initReactI18next } from 'react-i18next';

import type { Locale } from './index';
import { applyDocumentLocale, createI18n, detectLocale, dirOf } from './setup';

const STORAGE_KEY = 'iris.locale';

/** Saved choice (per device) or the browser language (BR-T-04). localStorage may be unavailable (private mode). */
export function initialLocale(): Locale {
  let saved: string | null = null;
  try {
    saved = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    saved = null;
  }
  return detectLocale(window.navigator.languages ?? [window.navigator.language], saved);
}

/** i18next instance with the React binding, `<html lang dir>` already set (call before the first render). */
export async function bootI18n(locale: Locale = initialLocale()): Promise<I18n> {
  const i18n = await createI18n(locale, [initReactI18next]);
  applyDocumentLocale(document, locale);
  return i18n;
}

interface LocaleContextValue {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  /** Switches FR ↔ AR at once, without a reload (US-C20); saved on this device. */
  setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ i18n, children }: { i18n: I18n; children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(i18n.language === 'ar' ? 'ar' : 'fr');

  useEffect(() => {
    const onChange = (lng: string) => {
      const next: Locale = lng === 'ar' ? 'ar' : 'fr';
      applyDocumentLocale(document, next);
      setLocaleState(next);
    };
    i18n.on('languageChanged', onChange);
    return () => i18n.off('languageChanged', onChange);
  }, [i18n]);

  const setLocale = useCallback(
    (next: Locale) => {
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // private mode: the choice lasts for this visit only
      }
      void i18n.changeLanguage(next);
    },
    [i18n],
  );

  const value = useMemo(() => ({ locale, dir: dirOf(locale), setLocale }), [locale, setLocale]);
  return (
    <I18nextProvider i18n={i18n}>
      <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
    </I18nextProvider>
  );
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useLocale must be used inside <LocaleProvider>');
  return value;
}

/** Re-exported so apps type the instance without depending on i18next directly. */
export type { i18n as I18n } from 'i18next';
